import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNativeBridge, useIsDesktop } from '@/lib/native-context';
import { useTenantWindowIcon } from '@/lib/use-tenant-window-icon';
import { usePrewarmCache } from '@/lib/use-prewarm-cache';
import i18n from 'i18next';

type PatchedFetch = typeof fetch & { __erpDesktopApiProxy?: true };

function apiPathForDesktopProxy(url: string): string | null {
  if (url.startsWith('/api/')) return url;
  try {
    const parsed = new URL(url, window.location.href);
    if (
      parsed.origin === window.location.origin &&
      parsed.pathname.startsWith('/api/')
    ) {
      return `${parsed.pathname}${parsed.search}`;
    }
  } catch {
    return null;
  }
  return null;
}

function mergeHeaders(
  input: RequestInfo | URL,
  init?: RequestInit,
): Array<[string, string]> {
  const headers = new Headers(input instanceof Request ? input.headers : undefined);
  if (init?.headers) {
    new Headers(init.headers).forEach((value, key) => headers.set(key, value));
  }
  return Array.from(headers.entries());
}

async function bodyToText(body: BodyInit | null | undefined): Promise<string | null> {
  if (body == null) return null;
  if (typeof body === 'string') return body;
  if (body instanceof URLSearchParams) return body.toString();
  if (body instanceof Blob) return body.text();
  if (body instanceof ArrayBuffer) return new TextDecoder().decode(body);
  if (ArrayBuffer.isView(body)) return new TextDecoder().decode(body);
  return null;
}

const DESKTOP_MENU_ROUTES: Record<string, string> = {
  'file:new-invoice': '/sales/invoices/new',
  'file:new-order': '/sales/orders/new',
  'file:new-delivery': '/sales/deliveries/new',
  'file:new-quote': '/sales/quotes/new',
  'file:new-article': '/articles?new=1',
  'file:new-client': '/clients?new=1',
  'file:new-supplier': '/suppliers?new=1',
  'file:new-payment': '/payments?new=1',
  'view:dashboard': '/',
  'view:caisse': '/caisse/pos',
  'nav:articles': '/articles',
  'nav:clients': '/clients',
  'nav:representatives': '/representatives',
  'nav:suppliers': '/suppliers',
  'nav:sales-quotes': '/sales/quotes',
  'nav:sales-orders': '/sales/orders',
  'nav:sales-deliveries': '/sales/deliveries',
  'nav:sales-invoices': '/sales/invoices',
  'nav:sales-credit-notes': '/sales/credit-notes',
  'nav:purchase-orders': '/purchases/orders',
  'nav:purchase-receptions': '/purchases/receptions',
  'nav:purchase-invoices': '/purchases/invoices',
  'nav:purchase-credit-notes': '/purchases/credit-notes',
  'nav:stock': '/stock',
  'nav:warehouses': '/stock/warehouses',
  'nav:inventories': '/stock/inventories',
  'nav:payments': '/payments',
  'nav:recurring-payments': '/payments/recurring',
  'nav:treasury': '/treasury',
  'nav:checks': '/checks',
  'nav:traites': '/traites',
  'nav:withholding-sales': '/withholding/sales',
  'nav:withholding-purchases': '/withholding/purchases',
  'nav:storefront': '/storefront',
  'nav:storefront-orders': '/storefront/orders',
  'nav:storefront-theme': '/storefront/theme',
  'nav:storefront-settings': '/storefront/settings',
  'nav:storefront-analytics': '/storefront/analytics',
  'nav:pro': '/pro',
  'nav:pro-customers': '/pro/customers',
  'nav:pro-products': '/pro/products',
  'nav:pro-commandes': '/pro/commandes',
  'nav:pro-wallets': '/pro/wallets',
  'nav:settings': '/settings',
  'nav:settings-company': '/settings/company',
  'nav:settings-branding': '/settings/branding',
  'nav:settings-modules': '/settings/modules',
  'nav:settings-database-import': '/settings/database-import',
  'nav:settings-pdf-templates': '/settings/pdf-templates',
  'nav:settings-delivery-providers': '/settings/delivery-providers',
  'nav:settings-app-builder': '/settings/app-builder',
  'nav:settings-pos-printers': '/settings/pos-printers',
};

/**
 * Mounts side-effects that only matter on desktop:
 *   - inbound deep links → router navigation
 *   - auto-updater check at boot
 *   - online/offline → toast
 *
 * Renders nothing. Lives inside the BrowserRouter so it can use useNavigate.
 */
export function DesktopShellHooks() {
  const isDesktop = useIsDesktop();
  const bridge = useNativeBridge();
  const navigate = useNavigate();
  // Push the tenant's logo to the title-bar icon whenever it changes.
  // Active in both web (favicon) and desktop (Win32 / NSWindow icon)
  // since the bridge has matching adapters for each platform.
  useTenantWindowIcon();

  // Prefetch the entities the user is most likely to need so the cache
  // is warm before they navigate. Without this, the very first visit to
  // (e.g.) /clients while offline shows blank because nothing was ever
  // cached for that key. With it, those queries land during the first
  // online session and stay in localStorage for 24h.
  usePrewarmCache();

  // Packaged Tauri builds are served from tauri://, so relative
  // Cloudflare Pages Function calls such as /api/storefront/sync-domain
  // and /api/delivery/intigo/* do not have a same-origin server. Route
  // those calls through the native bridge; browser/web builds keep using
  // normal fetch.
  useEffect(() => {
    if (!isDesktop) return;
    const currentFetch = window.fetch as PatchedFetch;
    if (currentFetch.__erpDesktopApiProxy) return;

    const originalFetch = window.fetch.bind(window);
    const proxiedFetch: PatchedFetch = (async (input, init) => {
      const rawUrl =
        typeof input === 'string'
          ? input
          : input instanceof URL
            ? input.toString()
            : input.url;
      const apiPath = apiPathForDesktopProxy(rawUrl);
      if (!apiPath) return originalFetch(input, init);

      const method = init?.method ?? (input instanceof Request ? input.method : 'GET');
      const body =
        init?.body !== undefined
          ? await bodyToText(init.body)
          : input instanceof Request
            ? await input.clone().text().catch(() => null)
            : null;
      const response = await bridge.apiProxy.fetch({
        path: apiPath,
        method,
        headers: mergeHeaders(input, init),
        body,
      });

      return new Response(response.body, {
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
      });
    }) as PatchedFetch;
    Object.defineProperty(proxiedFetch, '__erpDesktopApiProxy', { value: true });
    window.fetch = proxiedFetch;

    return () => {
      if (window.fetch === proxiedFetch) window.fetch = originalFetch;
    };
  }, [isDesktop, bridge.apiProxy]);

  // Intercept clicks on external `<a>` links so the OS browser opens
  // them — without this, target="_blank" is a no-op in the Tauri webview
  // and target="_self" would *replace the app* with the linked page,
  // wedging the user out of their session. Captured at the document
  // level so every existing link in the codebase works without
  // per-component changes.
  useEffect(() => {
    if (!isDesktop) return;
    const handler = (event: MouseEvent) => {
      // Honour modifier keys, middle-click etc.
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const target = event.target as Element | null;
      const anchor = target?.closest('a[href]') as HTMLAnchorElement | null;
      if (!anchor) return;
      const href = anchor.getAttribute('href') ?? '';
      // Only intercept absolute http(s) and mailto: — let the in-app
      // router handle relative paths and hash links.
      if (!/^(https?:|mailto:|tel:)/i.test(href)) return;
      event.preventDefault();
      void bridge.fileSystem.openExternal(href).catch((err) => {
        console.warn('[shell] openExternal failed', href, err);
      });
    };
    document.addEventListener('click', handler, { capture: true });
    return () => document.removeEventListener('click', handler, { capture: true });
  }, [isDesktop, bridge.fileSystem]);

  // Deep links — `erp://open?path=/sales/invoices/123` jumps straight to
  // the document. Restricted to known internal paths to avoid an attacker
  // crafting `erp://open?path=javascript:...` (router treats it as a path
  // anyway, but we belt-and-brace).
  useEffect(() => {
    if (!isDesktop) return;
    const off = bridge.deepLink.onOpen((url: string) => {
      try {
        const u = new URL(url);
        // Reject schemes we didn't register. Defence in depth — the OS
        // shouldn't dispatch anything else, but a malicious renderer
        // injection could still call this handler.
        if (u.protocol !== 'erp:' && u.protocol !== 'lyfty-erp:') return;
        const target = u.searchParams.get('path');
        if (
          target &&
          target.startsWith('/') &&
          !target.startsWith('//') && // protocol-relative URL
          !/[<>"'`\\]/.test(target) // shell/JSX injection chars
        ) {
          navigate(target);
        }
      } catch {
        // Bad URL — ignore.
      }
    });
    return off;
  }, [isDesktop, bridge, navigate]);

  // Updater — kick a check at startup. UI surfaces ready-to-install via
  // useUpdaterStatus (see the menu bar component).
  useEffect(() => {
    if (!isDesktop) return;
    void bridge.updater.check();
  }, [isDesktop, bridge]);

  // Native menu bar → router. Item ids match Rust menu.rs.
  useEffect(() => {
    if (!isDesktop) return;
    const off = bridge.menu.onItem((id) => {
      const route = DESKTOP_MENU_ROUTES[id];
      if (route) {
        navigate(route);
        return;
      }

      switch (id) {
        case 'file:new-invoice':
          navigate('/sales/invoices');
          // The list page surfaces the "New" CTA at the top — we just
          // get the user there. A future iteration can pre-open the
          // editor by passing state through the router.
          break;
        case 'file:new-quote':
          navigate('/sales/quotes');
          break;
        case 'file:print':
          window.print();
          break;
        case 'file:export-pdf':
          // Trigger a global event the document pages listen for.
          window.dispatchEvent(new CustomEvent('erp:menu:export-pdf'));
          break;
        case 'view:dashboard':
          navigate('/');
          break;
        case 'view:caisse':
          navigate('/caisse/pos');
          break;
        case 'view:lang': {
          const order = ['fr', 'ar', 'en'] as const;
          const cur = i18n.language?.slice(0, 2) ?? 'fr';
          const next = order[(order.indexOf(cur as (typeof order)[number]) + 1) % order.length];
          if (next) void i18n.changeLanguage(next);
          break;
        }
        case 'sync:flush':
          window.dispatchEvent(new CustomEvent('erp:sync:flush'));
          break;
        case 'sync:status':
          navigate('/settings/sync');
          break;
        case 'help:check-update':
          void bridge.updater.check();
          break;
        case 'help:docs':
          void bridge.fileSystem.openExternal('https://docs.lyfty.tn/erp');
          break;
        default:
          // Predefined items (cut/copy/paste/quit/etc.) are handled by
          // the OS — no React work needed.
          break;
      }
    });
    return off;
  }, [isDesktop, bridge, navigate]);

  // Online/offline transitions are surfaced via the network status hook
  // for the status bar; we don't toast here to avoid double notification
  // (the sync engine also emits events).

  return null;
}
